// @file Surface settings store
import { ALERT_ICON, CONFIRM_CANCEL_UPLOAD_SETTING, CONFIRM_DISCARD_SETTING_CHANGES } from '@@/bits/confirmation_dialog'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import type { User, WallBackground, WallLayoutAttributes, WallViz } from '@@/types'
import type { ContentModeration, GroupByTypes, Wall } from '@padlet/arvo'
import { isEqual } from 'lodash-es'
import { defineStore } from 'pinia'
import { SnackbarNotificationType, useGlobalSnackbarStore } from 'pinia/global_snackbar'
import { useSurfaceStore } from 'pinia/surface'
import { computed, toRefs } from 'vue'
import { OzConfirmationDialogBoxButtonScheme } from 'vuexstore/modules/global_confirmation_dialog'
import { SettingsSubpanel } from 'vuexstore/modules/surface_settings'

export const useSurfaceSettingsStore = defineStore('surfaceSettings', () => {
  const surfaceVuexStore = window.app?.$store // For gradual conversion to pinia
  const globalSnackbarStore = useGlobalSnackbarStore()

  // Access the surface store in a getters to avoid circular dependency.
  // https://pinia.vuejs.org/cookbook/composing-stores.html
  const surfaceStore = useSurfaceStore()
  const wallAttributes = computed<Partial<Wall>>(() => surfaceStore.wallAttributes)

  // getters
  const isPreviewing = computed<boolean>(() => surfaceVuexStore?.getters['settings/isPreviewing'])
  const wishGroupBy = computed<GroupByTypes>(() => surfaceVuexStore?.getters['settings/wishGroupBy'])
  const activePanel = computed<SettingsSubpanel | null>(() => surfaceVuexStore?.getters['settings/activePanel'])
  const wishSortBy = computed(() => surfaceVuexStore?.getters['settings/wishSortBy'])
  const previewAttributes = computed<Partial<Wall>>(() => surfaceVuexStore?.getters['settings/previewAttributes'])
  const hasUserSettingsChanged = computed(() => {
    const { background: currentBackground, ...previewWallttributesMinusBackground } = toRefs(previewAttributes?.value)
    const { background: originalBackground, ...originalWallAttributesMinusBackground } = toRefs(wallAttributes?.value)

    // we only need to check for url in background. we add attributes to selected background so it
    // will not have the exact same attributes as the wallpapers in the panel
    return (
      !isEqual(previewWallttributesMinusBackground, originalWallAttributesMinusBackground) ||
      currentBackground?.value?.url !== originalBackground?.value?.url
    )
  })

  const isReactionDataValid = computed<boolean>(() => surfaceVuexStore?.state.settings.isReactionDataValid)
  const isSavingSettings = computed<boolean>(() => surfaceVuexStore?.state.settings.isSaving)

  const showAuthor = computed<boolean>(() => previewAttributes?.value.show_author ?? false)
  const canConfigNewPostLocation = computed<boolean>(() => surfaceVuexStore?.getters.canConfigNewPostLocation)

  const isWallNameValid = computed<boolean>(() => surfaceVuexStore?.state.settings.isWallNameValid)
  const isWallNameAvailable = computed<boolean>(() => surfaceVuexStore?.state.settings.isWallNameAvailable)
  const contentModeration = computed<ContentModeration>(() => surfaceVuexStore?.getters['settings/contentModeration'])
  const contentModerationOptions = computed<ContentModeration[]>(() =>
    (surfaceVuexStore?.getters.isUsedInEducationalContext as boolean)
      ? ['none', 'auto', 'students_only', 'all']
      : ['none', 'auto', 'all'],
  )

  // In Shelf & old Timeline, the 'Group posts by section' field does not do anything so we hide the whole layout section.
  const canShowSectionLayoutSettings = computed(
    () => surfaceStore.canUseSections && surfaceStore.format !== 'shelf' && surfaceStore.format !== 'timeline',
  )

  const orderedLayouts: WallViz[] = ['grid', 'free', 'timeline_v2', 'matrix', 'stream', 'map']
  const layoutConversionOptions = computed<WallLayoutAttributes[]>(() => {
    const allLayouts = surfaceStore.allLayoutsByGroup
    return orderedLayouts.map((layout) => allLayouts.simple.find((l) => l.value === layout)) as WallLayoutAttributes[]
  })

  // actions
  function showConfirmDiscardSettingsDialog(): void {
    void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
      ...CONFIRM_DISCARD_SETTING_CHANGES,
      ...ALERT_ICON,
      afterDiscardActions: [{ type: 'settings/hideSettingsPanel' }],
      afterConfirmActions: [{ type: 'settings/saveSettings' }, { type: 'settings/hideSettingsPanel' }],
      buttonScheme: OzConfirmationDialogBoxButtonScheme.SaveDiscardCancel,
      forceFullWidthButtons: true,
      xShadow: true,
    })
  }

  function showConfirmCancelImageUploadSettingsDialog(
    params: { afterConfirmActions: Array<{ type: string; payload?: any }> } = {
      afterConfirmActions: [{ type: 'settings/hideSettingsPanel' }],
    },
  ): void {
    void surfaceVuexStore?.dispatch('confirmationDialog/openConfirmationDialog', {
      ...CONFIRM_CANCEL_UPLOAD_SETTING,
      ...ALERT_ICON,
      afterConfirmActions: params.afterConfirmActions,
      forceFullWidthButtons: true,
      xShadow: true,
    })
  }

  function hideSettingsPanel({ checkForUserSettings } = { checkForUserSettings: true }): void {
    if (checkForUserSettings && hasUserSettingsChanged.value) {
      showConfirmDiscardSettingsDialog()
    } else {
      void surfaceVuexStore?.dispatch('settings/hideSettingsPanel')
    }
  }

  function changeActivePanel(activePanel: SettingsSubpanel): void {
    void surfaceVuexStore?.dispatch('settings/changeActivePanel', activePanel)
  }

  function goBackToMainPanel(): void {
    void changeActivePanel(SettingsSubpanel.Main)
  }

  function updateBackground(background: Partial<WallBackground>, isCustom = false): void {
    let updatedBackground
    let updatedIsTitleBarTransparent

    if (isCustom) {
      updatedIsTitleBarTransparent = background.is_full_page
      updatedBackground = {
        ...background,
        effect: background.effects != null ? background.effects[0] : '',
        is_full_page: background.is_title_bar_transparent_by_default ?? false, // Default to false if it is a user selected image
        is_title_bar_transparent: updatedIsTitleBarTransparent,
      }
    } else {
      updatedIsTitleBarTransparent = true
      updatedBackground = {
        ...background,
        effect: '',
        effects: [],
        is_full_page: background.is_title_bar_transparent_by_default ?? true,
        is_title_bar_transparent: updatedIsTitleBarTransparent,
      }
    }
    updatePreviewAttributes({ background: updatedBackground, is_title_bar_transparent: updatedIsTitleBarTransparent })
  }

  function updatePreviewAttributes(attr: Record<string, any>): void {
    if (!isPreviewing.value) return
    const newPreviewAttributes: Partial<Wall> = { ...previewAttributes.value, ...attr }
    void surfaceVuexStore?.dispatch('settings/updateAndValidatePreviewAttributes', newPreviewAttributes)
  }

  function saveSettings(): void {
    void surfaceVuexStore?.dispatch('settings/updateWallValidity')
    void surfaceVuexStore?.dispatch('settings/saveSettings')
  }

  function updatePreviewAttributesAndSaveSettings(attr: Record<string, any>): void {
    updatePreviewAttributes(attr)
    saveSettings()
  }

  function saveSettingsAndGoBackToMainPanel(): void {
    saveSettings()
    goBackToMainPanel()
  }

  function resetSettings(): void {
    updatePreviewAttributes({ ...wallAttributes.value })
  }

  function discardChangesAndGoBackToMainPanel(): void {
    resetSettings()
    goBackToMainPanel()
  }

  function notifyWallSettingsUpdate(currentUserId: number, actor?: User): void {
    if (actor === undefined || actor.id === currentUserId || activePanel.value === null) {
      return
    }

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, @typescript-eslint/prefer-nullish-coalescing
    const user = actor.name || actor.username
    const message = user != null ? __('%{user} edited settings', { user }) : __('Someone edited settings')

    if (!globalSnackbarStore.isMessageBeingShown(message)) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message,
      })
    }
  }

  return {
    // getters
    isPreviewing,
    previewAttributes,
    wishGroupBy,
    wishSortBy,
    activePanel,
    isReactionDataValid,
    showAuthor,
    canConfigNewPostLocation,
    isSavingSettings,
    isWallNameValid,
    isWallNameAvailable,
    contentModeration,
    contentModerationOptions,
    layoutConversionOptions,
    canShowSectionLayoutSettings,

    // actions
    updatePreviewAttributes,
    updateBackground,
    changeActivePanel,
    goBackToMainPanel,
    hideSettingsPanel,
    saveSettings,
    saveSettingsAndGoBackToMainPanel,
    discardChangesAndGoBackToMainPanel,
    updatePreviewAttributesAndSaveSettings,
    notifyWallSettingsUpdate,
    showConfirmCancelImageUploadSettingsDialog,
  }
})
